import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { useToggle } from '@treezor/design-system';

import { updateUser } from '../store';

import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';
import { useDashboardConfig } from '@/hooks/useDashboardConfig';

import { useProfileContext } from '@/features/context/ProfileContext';
import { selectProfileByUserId } from '@/store/selectors';

export function useTag() {
  const { user } = useProfileContext();
  const { settings } = useDashboardConfig();
  const actions = settings?.users?.actions || [];

  const dispatch = useAppDispatch();

  const [isOnEditMode, toggleEditMode] = useToggle(false);

  const defaultValues = useMemo(() => ({ userTag: user.userTag }), [user.userTag]);

  const { register, handleSubmit, reset, watch, setValue } = useForm<{ userTag: string }>({
    defaultValues,
  });

  const isLoading = useAppSelector((state) => selectProfileByUserId(state, user!.userId)?.state?.user.isLoading);

  const updateTag = useCallback(() => {
    dispatch(updateUser({ userTag: watch('userTag') }, user.userId, () => toggleEditMode()));
  }, [dispatch, toggleEditMode, user.userId, watch]);

  const setTag = useCallback(
    (tag: string) => {
      setValue('userTag', tag);
    },
    [setValue],
  );

  const deleteTag = useCallback(() => {
    setValue('userTag', '');
    dispatch(updateUser({ userTag: watch('userTag') }, user.userId));
  }, [dispatch, setValue, user.userId, watch]);

  const onCancel = useCallback(() => {
    reset(defaultValues);
    toggleEditMode();
  }, [defaultValues, reset, toggleEditMode]);

  return {
    isDisabled: actions.includes('freezeUserTag'),
    isOnEditMode,
    register,
    toggleEditMode,
    handleSubmit: handleSubmit(updateTag),
    setTag,
    isLoading,
    deleteTag,
    userTagWatch: watch('userTag'),
    onCancel,
    user,
  };
}
